import { ReactNode } from "react";
import { getChildrenDisplayNameMap } from "./get-children-display-name-map";

/**
 * Separates children into two groups: those matching a specific displayName and those that don't.
 *
 * This function is useful for creating components with named slots where you want to extract
 * specific child components by their displayName while keeping all other children separate.
 *
 * @param children - The React children to filter
 * @param displayName - The component displayName to match against
 * @returns A tuple containing:
 *   - `[0]` (matched): Array of ReactNode elements with the specified displayName
 *   - `[1]` (unmatched): Array of all other ReactNode elements (including _unnamed children)
 *
 * @example
 * ```tsx
 * const Header = () => <div>Header</div>;
 * Header.displayName = "Header";
 *
 * const Footer = () => <div>Footer</div>;
 * Footer.displayName = "Footer";
 *
 * function MyComponent({ children }: { children: ReactNode }) {
 *   const [headers, rest] = getNamedChildren(children, "Header");
 *
 *   return (
 *     <div>
 *       <header>{headers}</header>
 *       <main>{rest}</main>
 *     </div>
 *   );
 * }
 *
 * // Usage:
 * <MyComponent>
 *   <Header />
 *   <p>This goes in main</p>
 *   <Footer />
 * </MyComponent>
 * // Result: Header in <header>, Footer and <p> in <main>
 * ```
 */
export const getNamedChildren = (children: ReactNode, displayName: string) => {
  const childrenMap = getChildrenDisplayNameMap(children);
  return Object.entries(childrenMap).reduce<[ReactNode[], ReactNode[]]>(
    (acc, [key, nodes]) => {
      if (key === displayName) {
        acc[0].push(...nodes);
        return acc;
      }
      acc[1].push(...nodes);
      return acc;
    },
    [[], []]
  );
};
